/* Grubwick admin — menu image/PDF capture/upload + extraction.
 *
 * Capture or upload menu photos or PDFs and pick a target restaurant first.
 * Extraction runs automatically as each file is added: images are resized,
 * PDFs read as-is, then POSTed to /gw/upload_menu (src/api/uploadMenu.ts), and
 * the returned items append to the "Extracted data" panel for review. The
 * capture UI is progressive — a full dropzone while empty, collapsing to a
 * compact thumbnail strip once files are in, so the review table (where the
 * real work happens) gets the room. Each thumbnail carries its own extraction
 * status (spinner → ✓ count → retry). Reviewing carries the edited items into
 * the restaurant editor to finish. */
import React, { useEffect, useRef, useState } from "react";
import type { Restaurant, MenuItemRecord } from "../../../data/adminData";
import { uploadMenu, type ExtractedMenu } from "../../../api/uploadMenu";
import { fileToDataUrl } from "../../../utils/image";
import { MenuItemsEditor } from "../MenuItemsEditor/MenuItemsEditor";
import { RestaurantPicker } from "../RestaurantPicker/RestaurantPicker";
import { useToast } from "../ui/ui";
import {
  Camera,
  Upload,
  X,
  Scan,
  Spinner,
  Check,
  Arrow,
  Plus,
  FileText,
  DropMascot,
} from "../icons/icons";

type ShotStatus = "extracting" | "done" | "error";
interface Shot {
  id: string;
  url: string;
  name: string;
  file: File;
  kind: "image" | "pdf";
  status: ShotStatus;
  count: number;
}

// Placeholder rows shown while a photo is being read — calmer than a spinner and,
// because they share the real table's grid, they show exactly where the
// extracted items will land. Reuses `.mtable`/`.mrow` so columns line up.
function ExtractSkeleton({ rows = 4 }: { rows?: number }): React.ReactElement {
  return (
    <div className="mtable skeleton" aria-hidden>
      <div className="mt-head">
        <span>Item</span>
        <span>Price</span>
        <span></span>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="mrow" key={i}>
          <div className="m-cell-nm">
            <span className="sk sk-nm" />
            <span className="sk sk-desc" />
          </div>
          <div className="m-cell-price">
            <span className="sk sk-price" />
          </div>
          <div className="mrow-actions" />
        </div>
      ))}
    </div>
  );
}

export function ImportView({
  restaurants,
  onGoEditor,
}: {
  restaurants: Restaurant[];
  onGoEditor: (target: string, items: MenuItemRecord[]) => void;
}): React.ReactElement {
  const [shots, setShots] = useState<Shot[]>([]);
  const [drag, setDrag] = useState(false);
  const [target, setTarget] = useState("new"); // 'new' | restaurant id
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState<ExtractedMenu | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Revoke object URLs for any shots still around at unmount.
  const shotsRef = useRef<Shot[]>(shots);
  shotsRef.current = shots;
  useEffect(
    () => () => shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url)),
    [],
  );

  // Number of extraction batches in flight, so concurrent uploads keep the
  // spinner on until the last one settles.
  const inflight = useRef(0);

  function patchShot(id: string, patch: Partial<Shot>) {
    setShots((s) => s.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  function addFiles(fileList: FileList | null) {
    const files = Array.from(fileList || []).filter(
      (f) => f.type.startsWith("image/") || f.type === "application/pdf",
    );
    if (!files.length) return;
    const next: Shot[] = files.map((f) => ({
      id: Math.random().toString(36).slice(2),
      url: URL.createObjectURL(f),
      name: f.name,
      file: f,
      kind: f.type === "application/pdf" ? "pdf" : "image",
      status: "extracting",
      count: 0,
    }));
    setShots((s) => [...s, ...next]);
    runExtraction(next, true); // auto-extract the photos just added
  }
  function remove(id: string) {
    setShots((s) => {
      const t = s.find((x) => x.id === id);
      if (t) URL.revokeObjectURL(t.url);
      return s.filter((x) => x.id !== id);
    });
  }

  // Resize each given photo, POST it to the extraction endpoint, and fold the
  // returned items into `result`. `append` keeps existing items (auto-extract on
  // upload) vs. replacing them (manual "Re-extract all"). Each photo's status is
  // tracked on its thumbnail so one failure is independently retryable. `name`
  // gives the model the target restaurant's name for context when extracting
  // against one.
  async function runExtraction(targetShots: Shot[], append: boolean) {
    if (!targetShots.length) return;
    const name =
      target === "new"
        ? null
        : (restaurants.find((r) => r.id === target)?.name ?? null);
    inflight.current++;
    setExtracting(true);
    setError(null);
    if (!append) {
      setResult(null);
      setShots((s) =>
        s.map((x) =>
          targetShots.some((t) => t.id === x.id)
            ? { ...x, status: "extracting", count: 0 }
            : x,
        ),
      );
    }
    try {
      let added = 0;
      for (const shot of targetShots) {
        try {
          const dataUrl = await fileToDataUrl(shot.file);
          const extracted = await uploadMenu(dataUrl, name);
          // Re-key items per shot so ids stay unique across photos (the mapper
          // numbers from zero within each image).
          const items = extracted.items.map((it, i) => ({
            ...it,
            id: `${shot.id}-${i}`,
          }));
          added += items.length;
          setResult((prev) => ({
            items: [...(prev?.items ?? []), ...items],
            transcript:
              (prev?.transcript ? prev.transcript + "\n\n" : "") +
              extracted.transcript,
          }));
          patchShot(shot.id, { status: "done", count: items.length });
        } catch (err) {
          patchShot(shot.id, { status: "error", count: 0 });
          setError(err instanceof Error ? err.message : "Extraction failed");
        }
      }
      if (added) toast(`Extracted ${added} item${added === 1 ? "" : "s"}`);
    } finally {
      inflight.current--;
      if (inflight.current === 0) setExtracting(false);
    }
  }

  const targetName =
    target === "new"
      ? null
      : (restaurants.find((r) => r.id === target)?.name ?? null);
  const itemCount = result?.items.length ?? 0;

  function pick(via: "camera" | "files") {
    (via === "camera" ? camRef : fileRef).current?.click();
  }

  return (
    <div className="page">
      <div className="phead">
        <div>
          <p className="h-eyebrow">Capture</p>
          <h1>Import from a menu</h1>
          <p className="sub">
            Snap or upload photos or a PDF of a menu and the items appear
            automatically, ready for you to review and edit.
          </p>
        </div>
      </div>

      <div className="import-flow">
        {/* hidden inputs shared by every capture entry point */}
        <input
          ref={camRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />

        {/* who the menu belongs to — chosen up front so it can give the model
            context and frame the whole flow */}
        <div className="apply-bar">
          <span className="ab-label">Save to</span>
          <RestaurantPicker
            value={target}
            restaurants={restaurants}
            onChange={setTarget}
          />
        </div>

        {shots.length === 0 ? (
          /* empty: dropzone is the hero. The whole zone is a button (tap-to-pick
             works on mobile, where drag-and-drop doesn't exist); the inner
             buttons stopPropagation so they keep their camera/files behaviour. */
          <div
            className={"dropzone" + (drag ? " drag" : "")}
            role="button"
            tabIndex={0}
            onClick={() => pick("files")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                pick("files");
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            <div className="dz-art">
              <DropMascot size={112} />
            </div>
            <h3>Drag &amp; drop a menu here (image or pdf)</h3>
            <p>
              or <span className="dz-link">click to browse</span> — clear,
              well-lit shots of each menu page (or a menu PDF) work best.
            </p>
            <div className="dz-actions">
              <button
                className="btn btn-accent"
                onClick={(e) => {
                  e.stopPropagation();
                  pick("camera");
                }}
              >
                <Camera size={16} /> Take photo
              </button>
              <button
                className="btn btn-ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  pick("files");
                }}
              >
                <Upload size={16} /> Upload files
              </button>
            </div>
            <span className="dz-formats">JPG · PNG · HEIC · PDF</span>
          </div>
        ) : (
          /* in progress: collapsed capture strip reclaims the space */
          <div
            className={"capture-strip" + (drag ? " drag" : "")}
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            <div className="cs-head">
              <span className="cs-t">
                <span className="ic">
                  <Camera size={16} />
                </span>{" "}
                Menu photos <span className="count-line">· {shots.length}</span>
              </span>
              <div className="cs-actions">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => pick("camera")}
                >
                  <Camera size={15} /> Take photo
                </button>
                <button
                  className="btn btn-soft btn-sm"
                  onClick={() => pick("files")}
                >
                  <Upload size={15} /> Add files
                </button>
              </div>
            </div>
            <div className="shots">
              {shots.map((s) => (
                <div className={"shot status-" + s.status} key={s.id}>
                  {s.kind === "pdf" ? (
                    <div className="shot-pdf" title={s.name}>
                      <FileText size={26} />
                      <span className="shot-pdf-nm">{s.name}</span>
                    </div>
                  ) : (
                    <img src={s.url} alt={s.name} />
                  )}
                  <button
                    className="rm"
                    onClick={() => remove(s.id)}
                    aria-label="Remove"
                  >
                    <X size={14} />
                  </button>
                  {s.status === "extracting" && (
                    <div className="shot-ov">
                      <span className="spin">
                        <Spinner size={22} />
                      </span>
                    </div>
                  )}
                  {s.status === "error" && (
                    <button
                      className="shot-ov err"
                      onClick={() => runExtraction([s], true)}
                    >
                      <Scan size={18} /> Retry
                    </button>
                  )}
                  {s.status === "done" && (
                    <div className="shot-cap">
                      <span className="ok">
                        <Check size={13} />
                      </span>
                      {s.count} item{s.count === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
              ))}
              <button className="cs-add" onClick={() => pick("files")}>
                <Plus size={18} /> Add photo
              </button>
            </div>
          </div>
        )}

        {/* extraction / review — the focus once photos are in */}
        {shots.length > 0 && (
          <div className="panel">
            <div className="panel-h">
              <h2>
                <span className="ic">
                  <Scan size={17} />
                </span>{" "}
                Extracted data
              </h2>
              {itemCount > 0 && (
                <span className="count-line">
                  {itemCount} item{itemCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="panel-b">
              {error && (
                <div className="result error" style={{ marginBottom: 16 }}>
                  <strong>Error:</strong> {error}
                </div>
              )}

              {result ? (
                <div className="extract-result">
                  <MenuItemsEditor
                    items={result.items}
                    onChange={(items) =>
                      setResult((r) => (r ? { ...r, items } : r))
                    }
                    emptyHint="No menu items were found — add them manually."
                  />
                </div>
              ) : extracting ? (
                <div className="extract-loading">
                  <p className="el-status">
                    <span className="spin">
                      <Spinner size={15} />
                    </span>{" "}
                    Reading the menu — items will appear here in a few seconds.
                  </p>
                  <ExtractSkeleton />
                </div>
              ) : (
                <div className="extract-empty">
                  <div className="ring">
                    <Scan size={24} />
                  </div>
                  <h3>Awaiting photos</h3>
                  <p>
                    Add a menu photo above and the items appear here
                    automatically.
                  </p>
                </div>
              )}

              <div className="extract-actions">
                <button
                  className="btn btn-primary"
                  disabled={extracting}
                  onClick={() => onGoEditor(target, result?.items ?? [])}
                >
                  <Arrow size={16} />{" "}
                  {targetName
                    ? `Review in ${targetName}`
                    : "Review & create restaurant"}
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={!shots.length || extracting}
                  onClick={() => runExtraction(shots, false)}
                >
                  <Scan size={16} /> Re-extract all
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
